import { Sizes } from '@/lib/constants/sizes'
import {
	Drawer,
	Stack,
	Box,
	Divider,
	SvgIcon,
	Typography,
	useTheme,
} from '@mui/material'
import React from 'react'
import { useNavigation } from '@/lib/context/navigationcontext'
import Image from 'next/image'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { Strings } from '@/lib/constants/strings'
import { useRouter } from 'next/navigation'
import { Button } from '@/lib/core-ui/button'
import { analyticsTags } from '@/app/lib/analytics/analyticsEventTags'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { trackLeftNavAnalyticsEvents } from './leftnavanalytics'
import NavItem, { NavIds, LeftNavProps } from './nav'
import CompanyName from './companyName'
import { useNavItems } from './useNavItems'

export default function LeftNavMobile({
	setOpen,
	changeFeaturePage,
	isTncSelected,
	setIsTncSelected,
	navItemSx,
}: Readonly<LeftNavProps>) {
	const theme = useTheme()
	const router = useRouter()
	const { tempBoss1863EnableCannyWidget } = useFlags()

	const {
		isLeftNavExpanded,
		toggleLeftNavExpanded,
		selectedNavItem,
		setSelectedNavItem,
		setSelectedAppBarNavItem,
		setSelectedNavItemBeforeSignout,
	} = useNavigation()

	const toggleNav = () => {
		if (isLeftNavExpanded) {
			toggleLeftNavExpanded?.(isLeftNavExpanded)
		}
	}

	const handleNavMenuClick = (item: NavItem) => {
		setIsTncSelected(false)
		trackLeftNavAnalyticsEvents(item.navId)

		if (item.navId === NavIds.Signout) {
			setSelectedNavItem(item)
			setSelectedNavItemBeforeSignout(selectedNavItem)
			setSelectedAppBarNavItem(selectedNavItem)
			setOpen(true)
		} else {
			setSelectedNavItem(item)
			setSelectedAppBarNavItem(item)
			changeFeaturePage(item.navId)
			toggleNav()
		}
	}

	function handleDrawerClose() {
		setSelectedNavItem(selectedNavItem)
		toggleNav()
	}

	const navItems = useNavItems()

	return (
		<Drawer
			variant={'temporary'}
			anchor="left"
			open={isLeftNavExpanded}
			onClose={() => handleDrawerClose()}
			PaperProps={{ elevation: 1 }}
			sx={{
				'display': { xs: 'block' },
				'& .MuiDrawer-paper': {
					width: `${Sizes.LEFT_DRAWER_WIDTH}px`,
					overflow: 'hidden',
				},
				'& .MuiPaper-elevation': { overflow: 'hidden' },
			}}
			data-testid="left-nav-mobile"
		>
			<Stack spacing={0}>
				<Stack display={'flex'}>
					<Box
						sx={{
							width: 'auto',
							height: 'auto',
							maxWidth: '100%',
							maxHeight: '100%',
							overflow: 'hidden',
							pt: 3,
							pl: 3,
						}}
					>
						<Image
							alt="logo"
							src={'/icons/bi_icons/broker-workplace_nav_expanded.svg'}
							width={128}
							height={48}
						/>
					</Box>
				</Stack>
				<CompanyName />
				{(() => {
					const filteredNavItems = navItems.filter(item => item.show)
					return filteredNavItems.map((item, index, array) => {
						const { label, iconSelected, iconDeselected, navId } = item
						const selected = selectedNavItem?.navId === navId
						const selectedColor = selected
							? theme.palette.foreground.brand.primary
							: theme.palette.foreground.neutral.onSurfaceVar02
						const icon = selected ? iconSelected : iconDeselected
						const isLastItem =
							index === array.length - (tempBoss1863EnableCannyWidget ? 2 : 1)
						return (
							<React.Fragment key={label}>
								<Stack spacing={1} data-testid="nav-button">
									{isLastItem && (
										<Stack>
											<Divider />
										</Stack>
									)}
									<Button
										variant="nav"
										color={selected ? 'selected' : 'default'}
										size={isLeftNavExpanded ? 'expanded' : 'collapsed'}
										LinkComponent={'a'}
										disableElevation
										data-testid={`nav-icon-button-${index}`}
										startIcon={
											<SvgIcon
												component={icon}
												data-testid="startIcon"
												aria-label={`${icon}`}
												sx={{
													color: selectedColor,
													width: 40,
													height: 24,
													transform: 'translateY(3px)',
												}}
											/>
										}
										onClick={() => handleNavMenuClick(item)}
										sx={navItemSx(selected)}
									>
										<Typography
											variant={TypographyVariants.baseRegular}
											sx={{
												color: selectedColor,
												textTransform: 'none',
											}}
											data-testid={'nav-icon-typography'}
											noWrap
										>
											{label}
										</Typography>
									</Button>
								</Stack>
							</React.Fragment>
						)
					})
				})()}
			</Stack>
			<Box
				sx={{
					width: `${Sizes.LEFT_DRAWER_WIDTH}px`,
					position: 'fixed',
					bottom: 0,
					left: 0,
					pb: 4,
				}}
			>
				<Button
					variant="nav"
					data-testid="tnc-nav-button"
					onClick={() => {
						toggleNav()
						setIsTncSelected(true)
						pushToDataLayer(analyticsTags.biViewTermsAndConditions)
						router.push('/feature/termsandconditions')
					}}
					color={isTncSelected ? 'selected' : 'default'}
					sx={{
						...navItemSx(isTncSelected),
						p: '10px 12px',
						width: '100%',
						justifyContent: 'flex-start',
					}}
				>
					<Typography variant={TypographyVariants.smallRegular}>
						{Strings.TNC_NAV_TITLE}
					</Typography>
				</Button>
			</Box>
		</Drawer>
	)
}
